import {
  Service,
  Template,
  TRUSTED_CFS,
  NON_CFS,
  CFS,
  NameContent,
  Config,
  ServiceConfigResponse,
  XmlFilesContent,
  BasicData,
  Divert,
  ADAPTER,
  BIGDATA_INTEGRATION,
  AuditConfig,
  CheckboxConfigType,
  AuthorizerBasicData,
  ValidatorBasicData,
  AnnotatorBasicData,
  AcceptorBasicData,
} from 'models/husqiotbus';
import {
  getSubDirectory,
  saveConfiguration,
  readFile,
  getFilesInDirectory,
  readBasicData,
  readAuditConfig,
} from './fileService';
import { generateXml } from './generateConfigService';

export async function saveHusqiotBusConfig(service: Service): Promise<void> {
  const data = {
    id: service.name,
    type: 'husqiotbusconfig',
    attributes: { service },
  };
  await saveConfiguration(data);
}

export async function readAllFiles(
  files: File[],
  prefix?: string,
): Promise<NameContent[]> {
  const results = await Promise.all(
    files.map(async (file) => {
      const fileContents = await readFile(file);
      return {
        name: prefix ? `${prefix}-${file.name}` : file.name,
        content: fileContents as string,
      };
    }),
  );
  return results;
}

async function getTemplates(
  subdirTemplates: FileSystemDirectoryHandle,
  directoryName: string,
): Promise<Template[]> {
  const directory = await getSubDirectory(directoryName, subdirTemplates);
  const files: File[] = await getFilesInDirectory(directory);
  const templates: Template[] = await readAllFiles(
    files,
    directoryName !== 'none-generated-configurations'
      ? directoryName
      : undefined,
  );
  return templates;
}

export async function readTemplatesFromFiles() {
  const configToolDir = await getSubDirectory('config-tool');
  const subdirTemplates = await getSubDirectory(
    'config-templates',
    configToolDir,
  );
  const templates: Template[] = [];
  const cfsTemplate = await getTemplates(subdirTemplates, CFS);
  const nonCfsTemplate = await getTemplates(subdirTemplates, NON_CFS);
  const trustedCfsTemplates = await getTemplates(subdirTemplates, TRUSTED_CFS);
  const adapterTemplates = await getTemplates(subdirTemplates, ADAPTER);
  const auditTemplates = await getTemplates(subdirTemplates, 'audit');
  const bigdataTemplates = await getTemplates(
    subdirTemplates,
    BIGDATA_INTEGRATION,
  );
  const orgConfigTemplates = await getTemplates(
    subdirTemplates,
    'none-generated-configurations',
  );
  templates.push(...cfsTemplate);
  templates.push(...nonCfsTemplate);
  templates.push(...trustedCfsTemplates);
  templates.push(...adapterTemplates);
  templates.push(...auditTemplates);
  templates.push(...bigdataTemplates);
  templates.push(...orgConfigTemplates);
  return templates;
}

export async function readConfigsFromFile(): Promise<Config[]> {
  const subdirConfigTool = await getSubDirectory('config-tool');
  const subdirConfigs = await getSubDirectory(
    'configurations',
    subdirConfigTool,
  );
  const files: File[] = await getFilesInDirectory(subdirConfigs);
  const configs: Config[] = await readAllFiles(files);

  return configs;
}

export function parseStoredConfig(configs: Config[], basicData: BasicData): Service[] {
  return configs.map((config) => {
    const response: ServiceConfigResponse = JSON.parse(
      config.content,
    ) as ServiceConfigResponse;
    
    const service = response.attributes.service;
    // Sync the stored configs with basicData if there have been any changes in basicData
    service.acceptors = syncConfigsWithBasicData(response.attributes.service.acceptors, basicData.acceptors);
    service.annotators = syncConfigsWithBasicData(response.attributes.service.annotators, basicData.annotators);
    service.authorizers = syncConfigsWithBasicData(response.attributes.service.authorizers, basicData.authorizers);
    service.validators = syncConfigsWithBasicData(response.attributes.service.validators, basicData.validators);
    // Skipped syncing addresses since they are in different orders in different config files making the generating file
    // move around addresses. If there will be a new address we should sync those as well
    // service.addresses = syncConfigsWithBasicData(response.attributes.service.addresses, basicData.addresses);
    service.existingDiverts = service.diverts;
    return service;
  });
}

function syncConfigsWithBasicData(storedConfigs: CheckboxConfigType[], basicDataConfigs: AcceptorBasicData[] | AuthorizerBasicData[] |  ValidatorBasicData[] | AnnotatorBasicData[]) {
  return basicDataConfigs.map((item) => {
    const stored = storedConfigs.find(
      (storedItem) => storedItem.name === item.name
    );
    if (stored) {
      return stored;
    }
    return { name: item.name, checked: false, disabled: false };
  });
}


export async function generateXmlFromTemplates(): Promise<XmlFilesContent> {
  const templates: Template[] = await readTemplatesFromFiles();
  const configs: Config[] = await readConfigsFromFile();
  const basicData: BasicData = await readBasicData();
  const services: Service[] = parseStoredConfig(configs, basicData);
  const auditConfig: AuditConfig = await readAuditConfig();
  return generateXml(services, templates, basicData, auditConfig);
}

export function getDivertVersion(
  oldDiverts: Divert[],
  serviceName: string,
  direction: string,
) {
  const existingDivert = oldDiverts.filter(
    (divert) => divert.serviceName === serviceName && divert.type === direction,
  );
  if (existingDivert.length > 0) {
    return `${Number(existingDivert[0].version || 0) + 1}`;
  }
  return '';
}
